import { useEffect, useState } from 'react';

// material-ui
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import PropTypes from 'prop-types';

// project-imports
import { devConsolelog } from 'helpers/utilityFunctions';
import { useDispatch, useSelector } from 'store';
import {
    getBookingListDataAndFilters,
    getMemberBookingsFilter,
    getMemberSearchesFilter,
    getMemberStats
} from 'store/slices/memberPriority';
import { BOOKINGS_TYPE, DATE_FILTER_TYPE, SEARCH_TYPE } from '../constant';

// third-party
import { format, isValid } from 'date-fns';

const initialDate = {
    start_date: null,
    end_date: null,
    isDateFilter: true
};

const DateTypeFilter = ({ type, isLoading, handleLoadingState }) => {
    const dispatch = useDispatch();
    const [dateFormData, setDateFormData] = useState({ ...initialDate });

    const memberPriorityData = useSelector((state) => state?.memberPriority);
    const { selectedMember = {}, memberSearchesFilter = {}, memberBookingsFilter = {} } = memberPriorityData || {};
    const { code: memberCode = '' } = selectedMember || {};

    devConsolelog('datetypefilter props', dateFormData, type, memberBookingsFilter);

    const fetchData = async () => {
        if (type === SEARCH_TYPE && isLoading) {
            await dispatch(getMemberStats(memberCode, memberSearchesFilter, SEARCH_TYPE));
        } else if (type === BOOKINGS_TYPE && isLoading) {
            await dispatch(getMemberStats(memberCode, memberBookingsFilter, BOOKINGS_TYPE));
        }
        handleLoadingState(false);
    };

    useEffect(() => {
        fetchData();
    }, [isLoading]);

    const handleDateChange = (value, dateType) => {
        devConsolelog('date filter change ', value, dateType);
        if (!isValid(value)) return;
        value = format(value, 'yyyy-MM-dd');
        const tempInitialDate = { ...dateFormData };
        tempInitialDate[dateType] = value;
        setDateFormData({ ...tempInitialDate });
        if (type === SEARCH_TYPE) {
            dispatch(getMemberSearchesFilter({ [DATE_FILTER_TYPE]: { ...tempInitialDate } }));
        } else if (type === BOOKINGS_TYPE) {
            dispatch(getMemberBookingsFilter({ [DATE_FILTER_TYPE]: { ...tempInitialDate } }));
            dispatch(getBookingListDataAndFilters(memberCode, '', { [DATE_FILTER_TYPE]: { ...tempInitialDate } }));
        }
        handleLoadingState(true);
    };

    useEffect(() => {
        if (type === SEARCH_TYPE) {
            setDateFormData(memberSearchesFilter[DATE_FILTER_TYPE] || initialDate);
        } else if (type === BOOKINGS_TYPE) {
            setDateFormData(memberBookingsFilter[DATE_FILTER_TYPE] || initialDate);
        }
    }, [memberSearchesFilter, memberBookingsFilter]);

    return (
        <Grid container item spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
            <Grid item sm={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DesktopDatePicker
                        inputFormat="dd/MM/yyyy"
                        renderInput={(props) => <TextField size="small" fullWidth {...props} helperText="" />}
                        label="Start Date"
                        value={dateFormData.start_date}
                        onChange={(newValue) => {
                            handleDateChange(newValue, 'start_date');
                        }}
                        disableFuture
                        maxDate={new Date(dateFormData.end_date)}
                    />
                </LocalizationProvider>
            </Grid>
            <Grid item sm={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DesktopDatePicker
                        inputFormat="dd/MM/yyyy"
                        renderInput={(props) => <TextField size="small" fullWidth {...props} helperText="" />}
                        label="End Date"
                        value={dateFormData.end_date}
                        onChange={(newValue) => {
                            handleDateChange(newValue, 'end_date');
                        }}
                        disableFuture
                        minDate={new Date(dateFormData.start_date)}
                    />
                </LocalizationProvider>
            </Grid>
        </Grid>
    );
};

DateTypeFilter.propTypes = {
    type: PropTypes.string,
    isLoading: PropTypes.bool,
    handleLoadingState: PropTypes.func
};

export default DateTypeFilter;
